/**
 * Utility type for component validation results
 */
export type ComponentValidationResult = {
  valid: boolean
  errors: string[]
}

/**
 * Comprehensive validation for component names
 * @param name - The component name to validate
 * @param options - Validation options
 * @returns Validation result with detailed feedback
 * @example
 * isValidComponentName("my-component") // { valid: true, errors: [] }
 * isValidComponentName("") // { valid: false, errors: ["Name cannot be empty"] }
 * isValidComponentName("My-Component") // { valid: false, errors: ["Name must be lowercase"] }
 */
export function isValidComponentName(
  name: string,
  options: {
    allowEmpty?: boolean
    minLength?: number
    maxLength?: number
  } = {}
): ComponentValidationResult {
  const { allowEmpty = false, minLength = 1, maxLength = 50 } = options

  const errors: string[] = []

  // Type check
  if (typeof name !== "string") {
    errors.push("Name must be a string")
    return { valid: false, errors }
  }

  // Empty check
  if (!allowEmpty && (!name || name.trim().length === 0)) {
    errors.push("Name cannot be empty")
    return { valid: false, errors }
  }

  // Length checks
  if (name.length < minLength) {
    errors.push(`Name must be at least ${minLength} characters long`)
  }

  if (name.length > maxLength) {
    errors.push(`Name must be no more than ${maxLength} characters long`)
  }

  // Format validation for kebab-case
  const kebabCaseRegex = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/
  if (!kebabCaseRegex.test(name)) {
    if (!/^[a-z]/.test(name)) {
      errors.push("Name must start with a lowercase letter")
    }
    if (/[A-Z]/.test(name)) {
      errors.push("Name must be lowercase (use kebab-case)")
    }
    if (/--/.test(name)) {
      errors.push("Name cannot contain consecutive dashes")
    }
    if (/[^a-z0-9-]/.test(name)) {
      errors.push("Name can only contain lowercase letters, numbers, and dashes")
    }
    if (/^-|-$/.test(name)) {
      errors.push("Name cannot start or end with a dash")
    }
  }

  // Reserved name checks
  const reservedNames = ["component", "element", "node", "ref", "key", "props"]
  if (reservedNames.includes(name)) {
    errors.push(`"${name}" is a reserved name`)
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
